import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';

const client = generateClient({ authMode: 'userPool' });

// voice options definition
const VOICE_OPTIONS: Record<
  string,
  { label: string; voices: { lang: string; options: string[] }[] }
> = {
  generative: {
    label: 'Generative',
    voices: [
        { lang: 'en-AU', options: ['Olivia'] },
        { lang: 'en-IN', options: ['Kajal'] },
        { lang: 'en-IE', options: ['Niamh'] },
        { lang: 'en-ZA', options: ['Ayanda'] },
        { lang: 'en-GB', options: ['Amy'] },
        { lang: 'en-US', options: ['Danielle', 'Joanna', 'Matthew', 'Ruth', 'Salli', 'Stephen'] },
        { lang: 'nl-BE', options: ['Lisa'] },
        { lang: 'nl-NL', options: ['Laura'] },
        { lang: 'fr-BE', options: ['Isabelle'] },
        { lang: 'fr-CA', options: ['Gabrielle', 'Liam'] },
        { lang: 'fr-FR', options: ['Celine', 'Lea', 'Remi'] },
        { lang: 'de-AT', options: ['Hannah'] },
        { lang: 'de-DE', options: ['Daniel', 'Vicki'] },
        { lang: 'it-IT', options: ['Bianca'] },
        { lang: 'ko-KR', options: ['Seoyeon'] },
        { lang: 'pl-PL', options: ['Ewa', 'Ola'] },
        { lang: 'pt-BR', options: ['Camila'] },
        { lang: 'es-MX', options: ['Andres', 'Mia'] },
        { lang: 'es-ES', options: ['Lucia', 'Sergio'] },
        { lang: 'es-US', options: ['Lupe', 'Pedro'] },
      ],
  },
  neural: {
    label: 'Neural',
    voices: [
      { lang: 'ar-AE', options: ['Hala', 'Zayd'] },
      { lang: 'nl-BE', options: ['Lisa'] },
      { lang: 'ca-ES', options: ['Arlet'] },
      { lang: 'cs-CZ', options: ['Jitka'] },
      { lang: 'yue-CN', options: ['Hiujin'] },
      { lang: 'cmn-CN', options: ['Zhiyu'] },
      { lang: 'da-DK', options: ['Sofie'] },
      { lang: 'nl-NL', options: ['Laura'] },
      { lang: 'en-AU', options: ['Olivia'] },
      { lang: 'en-GB', options: ['Amy', 'Emma', 'Brian', 'Arthur'] },
      { lang: 'en-IN', options: ['Kajal'] },
      { lang: 'en-IE', options: ['Niamh'] },
      { lang: 'en-NZ', options: ['Aria'] },
      { lang: 'en-SG', options: ['Jasmine'] },
      { lang: 'en-ZA', options: ['Ayanda'] },
      { lang: 'en-US', options: ['Danielle', 'Gregory', 'Ivy', 'Joanna', 'Kendra', 'Kimberly', 'Salli', 'Joey', 'Justin', 'Kevin', 'Matthew', 'Ruth', 'Stephen'] },
      { lang: 'fi-FI', options: ['Suvi'] },
      { lang: 'fr-BE', options: ['Isabelle'] },
      { lang: 'fr-CA', options: ['Gabrielle', 'Liam'] },
      { lang: 'fr-FR', options: ['Lea', 'Remi'] },
      { lang: 'de-DE', options: ['Vicki', 'Daniel'] },
      { lang: 'de-AT', options: ['Hannah'] },
      { lang: 'de-CH', options: ['Sabrina'] },
      { lang: 'hi-IN', options: ['Kajal'] },
      { lang: 'it-IT', options: ['Bianca', 'Adriano'] },
      { lang: 'ja-JP', options: ['Takumi', 'Kazuha', 'Tomoko'] },
      { lang: 'ko-KR', options: ['Seoyeon', 'Jihye'] },
      { lang: 'nb-NO', options: ['Ida'] },
      { lang: 'pl-PL', options: ['Ola'] },
      { lang: 'pt-BR', options: ['Camila', 'Vitoria', 'Thiago'] },
      { lang: 'pt-PT', options: ['Ines'] },
      { lang: 'es-ES', options: ['Lucia', 'Sergio'] },
      { lang: 'es-MX', options: ['Mia', 'Andres'] },
      { lang: 'es-US', options: ['Lupe', 'Pedro'] },
      { lang: 'sv-SE', options: ['Elin'] },
      { lang: 'tr-TR', options: ['Burcu'] },
    ],
  },
  standard: {
    label: 'Standard',
    voices: [
      { lang: 'arb', options: ['Zeina'] },
      { lang: 'cmn-CN', options: ['Zhiyu'] },
      { lang: 'da-DK', options: ['Naja', 'Mads'] },
      { lang: 'nl-NL', options: ['Lotte', 'Ruben'] },
      { lang: 'en-AU', options: ['Nicole', 'Russell'] },
      { lang: 'en-GB', options: ['Amy', 'Emma', 'Brian'] },
      { lang: 'en-IN', options: ['Aditi', 'Raveena'] },
      { lang: 'en-US', options: ['Ivy', 'Joanna', 'Kendra', 'Kimberly', 'Salli', 'Joey', 'Kevin'] },
      { lang: 'en-GB-WLS', options: ['Geraint'] },
      { lang: 'fr-FR', options: ['Celine', 'Lea', 'Mathieu'] },
      { lang: 'fr-CA', options: ['Chantal'] },
      { lang: 'de-DE', options: ['Marlene', 'Vicki', 'Hans'] },
      { lang: 'hi-IN', options: ['Aditi'] },
      { lang: 'is-IS', options: ['Dora', 'Karl'] },
      { lang: 'it-IT', options: ['Carla', 'Bianca', 'Giorgio'] },
      { lang: 'ja-JP', options: ['Mizuki', 'Takumi'] },
      { lang: 'ko-KR', options: ['Seoyeon'] },
      { lang: 'nb-NO', options: ['Liv'] },
      { lang: 'pl-PL', options: ['Ewa', 'Maja', 'Jacek', 'Jan'] },
      { lang: 'pt-BR', options: ['Camila', 'Vitoria', 'Ricardo'] },
      { lang: 'pt-PT', options: ['Ines', 'Cristiano'] },
      { lang: 'ro-RO', options: ['Carmen'] },
      { lang: 'ru-RU', options: ['Tatyana', 'Maxim'] },
      { lang: 'es-ES', options: ['Conchita', 'Lucia', 'Enrique'] },
      { lang: 'es-MX', options: ['Mia'] },
      { lang: 'es-US', options: ['Lupe', 'Penelope', 'Miguel'] },
      { lang: 'sv-SE', options: ['Astrid'] },
      { lang: 'tr-TR', options: ['Filiz'] },
      { lang: 'cy-GB', options: ['Gwyneth'] },
    ],
  },
};

const SPEED_OPTIONS = [
  { value: 'x-slow', label: 'Extra Slow (50% speed)' },
  { value: 'slow', label: 'Slow (75% speed)' },
  { value: 'medium', label: 'Medium (100% speed)' },
  { value: 'fast', label: 'Fast (125% speed)' },
  { value: 'x-fast', label: 'Extra Fast (150% speed)' },
];

const ENGINE_INFO: Record<string, { label: string; description: string }> = {
  generative: {
    label: 'Generative',
    description: 'High quality gen-AI generated voices'
  },
  neural: {
    label: 'Neural',
    description: 'Good quality neural-network-AI generated voices'
  },
  standard: {
    label: 'Standard',
    description: 'Simplest voices, no AI (concatenates phonemes of recorded speech)'
  }
};

interface Props {
  apiKey: string;
  initialConfig: string | null;
  onConfigSave?: (config: string) => void;
  isLoggedIn: boolean;
}

const SessionConfig: React.FC<Props> = ({ apiKey, initialConfig, onConfigSave, isLoggedIn }) => {
  const [voiceEngine, setVoiceEngine] = useState<'generative' | 'neural' | 'standard'>('neural');
  const [language, setLanguage] = useState<string>('');
  const [voice, setVoice] = useState<string>('');
  const [speed, setSpeed] = useState<string>('medium');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);

  // Initialize from saved config
  useEffect(() => {
    if (!initialized) {
      if (initialConfig) {
        try {
          const config = JSON.parse(initialConfig);
          if (config.engine) setVoiceEngine(config.engine);
          if (config.language) setLanguage(config.language);
          if (config.voice) setVoice(config.voice);
          if (config.speed) setSpeed(config.speed);
          setInitialized(true);
        } catch (err) {
          console.error('Failed to parse initial config:', err);
        }
      } else {
        // No initial config - set defaults and auto-save
        const defaultLang = VOICE_OPTIONS[voiceEngine].voices[0].lang;
        const defaultVoice = VOICE_OPTIONS[voiceEngine].voices[0].options[0];
        setLanguage(defaultLang);
        setVoice(defaultVoice);
        setInitialized(true);

        // Auto-save default config for existing users without config
        setTimeout(async () => {
          try {
            const session = await fetchAuthSession();
            if (!session?.tokens?.accessToken) return;

            const mutation = /* GraphQL */ `
              mutation UpdateTTSConfig($ttsConfig: String!) {
                updateTTSConfig(ttsConfig: $ttsConfig) {
                  apiKey
                  ttsConfig
                }
              }
            `;

            const ttsConfig = {
              engine: voiceEngine,
              language: defaultLang,
              voice: defaultVoice,
              speed,
            };

            await client.graphql({
              query: mutation,
              variables: { ttsConfig: JSON.stringify(ttsConfig) },
              authToken: session.tokens.accessToken.toString(),
            });

            console.log('✅ Auto-saved default TTS config');
          } catch (err) {
            console.error('❌ Failed to auto-save default config:', err);
          }
        }, 100);
      }
    }
  }, [initialConfig, initialized, voiceEngine, speed]);

  // Update selected language automatically when engine changes
  useEffect(() => {
    // Don't run this effect until initialization is complete
    if (!initialized) return;

    // Check if current language exists in new engine
    const availableLangs = VOICE_OPTIONS[voiceEngine].voices.map((v) => v.lang);
    if (!availableLangs.includes(language)) {
      const defaultLang = VOICE_OPTIONS[voiceEngine].voices[0].lang;
      setLanguage(defaultLang);
      setVoice(VOICE_OPTIONS[voiceEngine].voices[0].options[0]);
    } else {
      // Language exists, check if voice exists
      const langVoices = VOICE_OPTIONS[voiceEngine].voices.find((v) => v.lang === language)?.options || [];
      if (!langVoices.includes(voice)) {
        setVoice(langVoices[0] || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceEngine, initialized]);

  // Update voice when language changes
  useEffect(() => {
    // Don't run this effect until initialization is complete
    if (!initialized) return;

    if (language) {
      const langVoices = VOICE_OPTIONS[voiceEngine].voices.find((v) => v.lang === language)?.options || [];
      if (!langVoices.includes(voice)) {
        setVoice(langVoices[0] || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, initialized]);

  const handlePreview = () => {
    setPreviewPlaying(true);
    const audio = new Audio(`/previews/${voiceEngine}_${language.replace(/-/g, '_')}_${voice}.mp3`);
    audio.play().then(() => {
      // playing
    }).catch((err) => {
      console.error('Failed to play preview:', err);
      setPreviewPlaying(false);
    });
    audio.onended = () => setPreviewPlaying(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        throw new Error('No access token found.');
      }

      const mutation = /* GraphQL */ `
        mutation UpdateTTSConfig($ttsConfig: String!) {
          updateTTSConfig(ttsConfig: $ttsConfig) {
            apiKey
            ttsConfig
          }
        }
      `;

      const ttsConfig = {
        engine: voiceEngine,
        language,
        voice,
        speed,
      };

      const result: any = await client.graphql({
        query: mutation,
        variables: { ttsConfig: JSON.stringify(ttsConfig) },
        authToken: session.tokens.accessToken.toString(),
      });

      console.log('✅ updateTTSConfig result:', result);
      setSaveStatus('Configuration saved successfully.');

      // Update parent component state with new config
      if (onConfigSave) {
        onConfigSave(JSON.stringify(ttsConfig));
      }
    } catch (err: any) {
      console.error('❌ Failed to save config:', err);
      setSaveStatus(`Error saving configuration: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const availableLangs = VOICE_OPTIONS[voiceEngine].voices.map((v) => v.lang);
  const availableVoices =
    VOICE_OPTIONS[voiceEngine].voices.find((v) => v.lang === language)?.options || [];

  return (
    <div className="config-card">
      <h2>TTS Configuration</h2>
      <p className="config-subtitle">TTS requests sent with the plugin will use these values</p>

      <div className="form-group">
        <label>Voice Engine</label>
        <select
          value={voiceEngine}
          onChange={(e) => setVoiceEngine(e.target.value as any)}
        >
          <option value="generative">{ENGINE_INFO.generative.label}</option>
          <option value="neural">{ENGINE_INFO.neural.label}</option>
          <option value="standard">{ENGINE_INFO.standard.label}</option>
        </select>
        <p className="field-description">{ENGINE_INFO[voiceEngine].description}</p>
      </div>

      <div className="form-group">
        <label>Language</label>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}>
          {availableLangs.map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Voice</label>
        <select value={voice} onChange={(e) => setVoice(e.target.value)}>
          {availableVoices.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Speed</label>
        <select value={speed} onChange={(e) => setSpeed(e.target.value)}>
          {SPEED_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <button onClick={handlePreview} disabled={previewPlaying || !voice || !language || !voiceEngine}>
        {previewPlaying ? 'Playing preview...' : 'Listen to preview'}
      </button>

      <button onClick={handleSave} disabled={saving || !isLoggedIn}>
        {saving ? 'Saving...' : isLoggedIn ? 'Save Configuration' : 'Sign in to save configuration'}
      </button>

      {saveStatus && <p className="save-status">{saveStatus}</p>}
    </div>
  );
};

export default SessionConfig;
